// Page Catalogue : le registre du tableau de service. Les 367 items en lignes denses, fouillées au clavier (numéro,
// titre, matière, rang), dépliables sur place (sous-parties, collèges, longueur de la fiche, place dans la boucle) ;
// d'ici on met un item au tableau (un J0 à une date) sans quitter la page.
// Le cadre (renderCatalogue) et les lignes (renderCatalogueList) sont rendus séparément, comme dans le rendu
// historique que ce module remplace : taper dans la recherche ne redessine que les lignes, le focus reste au champ.
import { html, attr, brut } from '../../core/html.js';
import { state } from '../../core/store.js';
import { actions } from '../../core/actions.js';
import { ico } from '../icones.js';
import { ouvrirMenu, ouvrirFormulaire } from '../components/menu.js';

var ouverts = {};          // items dépliés (le module survit aux redessins, pas au rechargement)
var focusARestaurer = null; // sélection du champ de recherche quand la page entière se redessine pendant la frappe
var posee = null;          // { cle, depuis } : aimant « Planifié » qui vient de se poser (entrée courte, reprise par délai négatif)
var renduPrevu = 0;        // rAF en attente pour la frappe

// Deux icônes Phosphor (regular, MIT) dessinées en ligne : le sprite partagé n'est pas touché par ce lot.
var TRACES = {
    'magnifying-glass': 'M229.66,218.34l-50.07-50.06a88.11,88.11,0,1,0-11.31,11.31l50.06,50.07a8,8,0,0,0,11.32-11.32ZM40,112a72,72,0,1,1,72,72A72.08,72.08,0,0,1,40,112Z',
    'arrow-elbow-down-right': 'M221.66,181.66l-48,48a8,8,0,0,1-11.32-11.32L196.69,184H72a8,8,0,0,1-8-8V32a8,8,0,0,1,16,0V168H196.69l-34.35-34.34a8,8,0,0,1,11.32-11.32l48,48A8,8,0,0,1,221.66,181.66Z',
};
function icoLocal(nom, taille) {
    return brut('<svg class="ico" width="' + (taille || 20) + '" height="' + (taille || 20) + '" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true" focusable="false"><path d="' + TRACES[nom] + '"/></svg>');
}

var RANGS = [['all', 'Tous les rangs'], ['3', 'Rang or'], ['2', 'Incontournable'], ['1', 'Important'], ['0', 'Secondaire']];
function pluriel(n, un, des) { return n + ' ' + (n > 1 ? des : un); }
function normaliser(s) { return String(s == null ? '' : s).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, ''); }
function dateValide(d) { return !!d && /^\d{4}-\d{2}-\d{2}$/.test(d); }
function refocus(sel) { requestAnimationFrame(function() { var el = document.querySelector(sel); if (el && el.focus) { try { el.focus({ preventScroll: true }); } catch (e) {} } }); }
function mouvementReduit() { try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; } }

// --- lecture de l'état d'un item
function tachesDe(item) { var id = String(item.id); return state.tasks.filter(function(t) { return String(t.baseItemId || t.itemId) === id && !t.isDouble; }); }
// Sous-parties : celles enregistrées (state.subParts) d'abord ; sinon celles que le planning porte déjà (item scindé
// dont le découpage n'est plus mémorisé) ; sinon aucune.
function partiesDe(item, taches) {
    var carte = {}; taches.forEach(function(t) { carte[t.itemId] = t.titre; });
    var ids = Object.keys(carte).filter(function(k) { return k !== String(item.id); });
    var saved = state.subParts[item.id] || [];
    if (saved.length > 1) {
        // Une sous-partie déjà au planning garde l'identifiant de ses séances (Reprogrammer les remplace, il n'en
        // ajoute pas une seconde série) ; une sous-partie jamais planifiée reçoit un identifiant stable par rang.
        return saved.map(function(nom, idx) {
            var k = ids.find(function(k) { return String(carte[k]).indexOf('[' + nom + ']') !== -1; }) || ids.find(function(k) { return String(carte[k]).indexOf(nom) !== -1; });
            return { pId: k || (item.id + '_p' + idx), nom: nom, titre: item.titre + ' [' + nom + ']', planifiee: !!k, memorisee: true };
        });
    }
    if (ids.length > 1) return ids.map(function(pId) { return { pId: pId, nom: String(carte[pId]).replace(/^.*\[(.*)\]\s*$/, '$1'), titre: carte[pId], planifiee: true, memorisee: false }; });
    return [];
}
function boucleDe(taches) {
    if (!taches.length) return null;
    var td = window.todayStr(), tri = taches.slice().sort(function(a, b) { return a.date.localeCompare(b.date); });
    var faites = tri.filter(function(t) { return t.status === 'done'; }).length;
    var prochaine = tri.find(function(t) { return t.status === 'pending' && t.date >= td; }) || tri.find(function(t) { return t.status === 'pending'; }) || null;
    var j0 = tri.find(function(t) { return t.type === 'J0'; }) || tri[0];
    return { total: tri.length, faites: faites, prochaine: prochaine, j0: j0, retard: prochaine ? prochaine.date < td : false };
}
function correspond(item, q, sc) {
    if (sc !== 'all' && item.score !== parseInt(sc, 10)) return false;
    if (!q) return true;
    if (String(item.id).indexOf(q) !== -1 || normaliser(item.titre).indexOf(q) !== -1 || normaliser(item.matiere).indexOf(q) !== -1) return true;
    return item.collegesSecondaires.some(function(c) { return normaliser(c).indexOf(q) !== -1; });
}
function planningBloque() { return !!state.planningIndisponible; }

// --- aimants d'une ligne
function puceMatiere(m, secondaire) { return html`<span class="puce puce-sans-point ${window.getSpecialtyStyle(m)}${secondaire ? ' cat-mat-sec' : ''}"${secondaire ? html` title="Collège secondaire"` : ''}>${m}</span>`; }
function puceRang(score) { return html`<span class="puce puce-sans-point ${window.getBadgeStyle(score)}" title="Rang de priorité de l'item">${window.getScoreLabel(score)}</span>`; }
function puceDensite(id) {
    var fd = window.getFicheData(id); if (!fd || !fd.w) return '';
    var palier = window.getLengthTier(fd.w), mins = window.estimateReadingMinutes(fd.w);
    return html`<span class="puce puce-sans-point ${palier.class}" title="Fiche de ${fd.w} mots hors rang C · ≈ ${mins} min de lecture à 130 mots/min">${palier.label} · ${window.formatDuration(mins)}</span>`;
}
function pucePlanifie(cle, titre) {
    var entree = posee && posee.cle === cle ? posee.delai : null;
    return html`<span class="puce puce-accent cat-planifie${entree != null ? ' cat-planifie--posee' : ''}"${entree ? html` style="--pose-delai:${entree}ms"` : ''}${titre ? html` title="${titre}"` : ''}>Planifié</span>`;
}
function boutonPlanifier(item, pId, titre, deja, classe, nParties) {
    var bloque = planningBloque(), aide;
    if (bloque) aide = 'Le planning n\'a pas pu être chargé : recharge la page avant de le modifier';
    else if (deja) aide = 'Remplacer ses séances par une nouvelle séquence à partir d\'une date';
    else if (nParties > 1) aide = 'Mettre les ' + nParties + ' sous-parties au planning, un J0 par jour, puis leurs révisions';
    else aide = 'Un J0 à la date choisie, puis les révisions J1, J3, J7… selon le rang';
    return html`<button type="button" class="btn ${deja ? 'btn-fantome' : 'btn-secondaire'} btn-sm ${classe || ''}" data-action="catPlanifier" data-args="${attr([String(item.id), pId || null, titre || null])}" data-planifier="${pId || item.id}"${bloque ? html` disabled aria-disabled="true"` : ''} title="${aide}">${deja ? 'Reprogrammer' : '+ J0'}</button>`;
}

// --- une ligne du registre
function ligne(item) {
    var id = String(item.id), taches = tachesDe(item), parties = partiesDe(item, taches), boucle = boucleDe(taches);
    var ouvert = !!ouverts[id], fiche = window.getFicheDirectLink(id), fd = window.getFicheData(id);
    var planifie = taches.length > 0, planifiable = parties.length === 0 || parties[0].memorisee;
    var prochaine = boucle && boucle.prochaine ? (boucle.prochaine.type + ' le ' + window.formatDateFr(boucle.prochaine.date)) : '';
    return html`<div class="cat-item${ouvert ? ' cat-item--ouvert' : ''}${planifie ? ' cat-item--planifie' : ''}" role="listitem" data-item="${id}">
        <button type="button" class="cat-item-toggle" data-action="catBasculer" data-args="${attr([id])}" aria-expanded="${ouvert ? 'true' : 'false'}" aria-controls="cat-detail-${id}">
            ${ico('caret-right', { taille: 16, classe: 'cat-caret' })}
            <span class="cat-item-num num">${id}</span>
            <span class="cat-item-titre">${item.titre}</span>
        </button>
        <div class="cat-item-meta">
            ${puceMatiere(item.matiere)}
            ${item.collegesSecondaires.map(function(c) { return puceMatiere(c, true); })}
            ${item.collegesSecondaires.length ? html`<span class="puce puce-sans-point cat-mat-plus" title="Collèges secondaires : ${item.collegesSecondaires.join(', ')}">+${pluriel(item.collegesSecondaires.length, 'collège', 'collèges')}</span>` : ''}
            ${puceRang(item.score)}
            ${puceDensite(id)}
            ${parties.length ? html`<span class="puce puce-sans-point" title="Item scindé : une séance par sous-partie">${pluriel(parties.length, 'sous-partie', 'sous-parties')}</span>` : ''}
            ${planifie ? pucePlanifie(id, prochaine ? 'Prochaine séance : ' + prochaine : '') : ''}
        </div>
        <div class="cat-item-actions">
            ${fiche ? html`<a class="btn btn-fantome btn-sm" href="${fiche}" target="_blank" rel="noopener" title="Ouvrir la fiche PDF de l'item ${id}">${ico('file-text', { taille: 16 })}<span class="cat-action-texte">Fiche</span></a>` : ''}
            <button type="button" class="btn btn-fantome btn-sm" data-action="catQcm" data-args="${attr([id])}" title="QCM, flashcards et active recall de l'item ${id}">${ico('question', { taille: 16 })}<span class="cat-action-texte">QCM</span></button>
            ${planifiable ? boutonPlanifier(item, null, null, planifie, 'cat-j0', parties.length)
                : html`<button type="button" class="btn btn-secondaire btn-sm cat-j0" disabled aria-disabled="true" title="Item scindé : déplie la ligne et planifie chaque sous-partie depuis sa propre rangée">+ J0</button>`}
            <button type="button" class="ico-btn cat-item-menu" data-action="catMenu" data-args="${attr([id])}" aria-haspopup="menu" aria-expanded="false" aria-label="Autres actions pour l'item ${id}">${ico('dots-three-outline')}</button>
        </div>
        <div class="cat-item-detail" id="cat-detail-${id}"${ouvert ? '' : html` inert`}><div class="cat-item-fiche">
            ${parties.length ? html`<ul class="cat-parties" aria-label="Sous-parties de l'item ${id}">${parties.map(function(p) {
                return html`<li class="cat-partie">
                    <span class="cat-partie-nom">${icoLocal('arrow-elbow-down-right', 16)}${p.nom}</span>
                    ${p.planifiee ? pucePlanifie(p.pId) : ''}
                    <span class="cat-partie-actions">
                        <button type="button" class="btn btn-fantome btn-sm" data-action="catQcm" data-args="${attr([id, p.nom])}" title="QCM, flashcards et active recall de cette sous-partie">${ico('question', { taille: 16 })}QCM</button>
                        <button type="button" class="btn btn-fantome btn-sm" data-action="catSommaire" data-args="${attr([id, p.nom])}" title="Sommaire NotebookLM de cette sous-partie">${ico('list-bullets', { taille: 16 })}Sommaire</button>
                        ${boutonPlanifier(item, p.pId, p.titre, p.planifiee)}
                    </span>
                </li>`;
            })}</ul>` : ''}
            <dl class="cat-fiche">
                <dt>Collèges</dt><dd>${item.matiere}${item.collegesSecondaires.length ? html` · ${item.collegesSecondaires.join(', ')}` : ''}</dd>
                <dt>Fiche</dt><dd class="num">${fd && fd.w ? html`${fd.w} mots · ≈ ${window.formatDuration(window.estimateReadingMinutes(fd.w))} de lecture` : 'Longueur non mesurée'}${fd && fd.parts && fd.parts.length >= 2 ? html` · ${pluriel(fd.parts.length, 'grande partie', 'grandes parties')}` : ''}</dd>
                <dt>Planning</dt><dd class="num">${boucle ? html`J0 le ${window.formatDateFr(boucle.j0.date)} · ${boucle.faites} / ${boucle.total} séances faites${boucle.prochaine ? html` · ${boucle.retard ? html`<span class="cat-retard">en retard : ${prochaine}</span>` : html`prochaine ${prochaine}`}` : ' · séquence terminée'}` : 'Pas au planning'}</dd>
            </dl>
        </div></div>
    </div>`;
}

function squeletteLignes() {
    return html`<div class="squelettes" aria-hidden="true">${[1, 2, 3, 4, 5, 6].map(function() { return html`<div class="cat-item cat-item--squelette"><div class="cat-item-toggle"><div class="squelette squelette-ligne" style="width:55%"></div></div><div class="cat-item-meta"><div class="squelette squelette-ligne" style="width:30%;height:18px"></div></div></div>`; })}</div>`;
}
// Le compte : une énumération télégraphique au pluriel (« 45 items · rang or »), une phrase au singulier de zéro
// (« Aucun item de rang or pour « zzzz » ») — le point médian ne sait pas qualifier un nom.
function texteCompte(n, q, sc) {
    var t = n ? pluriel(n, 'item', 'items') : 'Aucun item';
    if (sc !== 'all') {
        var rang = window.getScoreLabel(parseInt(sc, 10)).toLowerCase();
        t += n ? ' · ' + rang : (rang.indexOf('rang') === 0 ? ' de ' : ' ') + rang;
    }
    if (q) t += ' pour « ' + q + ' »';
    return t;
}

// Le compte se met à jour à l'œil à chaque caractère frappé, mais ne s'annonce qu'après une pause : sinon le
// lecteur d'écran lit tous les états intermédiaires (« 43 items pour « e » », « 12… », « 1… »).
var annonceMinuteur = 0, annonceOuverte = false;
function annoncerCompte(texte, filtre) {
    if (!annonceOuverte) { if (!filtre) return; annonceOuverte = true; }   // au chargement, la page ne s'annonce pas elle-même
    if (annonceMinuteur) clearTimeout(annonceMinuteur);
    annonceMinuteur = setTimeout(function() {
        annonceMinuteur = 0;
        var el = document.getElementById('cat-compte-annonce');
        if (el) el.textContent = texte;
    }, 400);
}

// --- le cadre : en-tête, barre d'outils collante, conteneur des lignes
export function renderCatalogue() {
    var actif = document.activeElement;
    if (actif && actif.id === 'cat-search') focusARestaurer = { debut: actif.selectionStart, fin: actif.selectionEnd };
    var items = window.ednsItems, matieres = {}, planifies = 0;
    items.forEach(function(it) { matieres[it.matiere] = true; if (tachesDe(it).length) planifies++; });
    var q = state.searchQuery || '', sc = state.filterScore || 'all';
    return html`<div class="cat fade-in">
        <header class="jour-entete cat-entete">
            <div class="jour-entete-texte">
                <h1 class="jour-date">Catalogue</h1>
                <p class="jour-resume num">${pluriel(items.length, 'item', 'items')} · ${pluriel(Object.keys(matieres).length, 'matière', 'matières')} · ${planifies ? pluriel(planifies, 'au planning', 'au planning') : 'aucun au planning'}</p>
            </div>
        </header>
        ${planningBloque() ? html`<p class="cat-note"><span class="puce puce-warn">Planning bloqué</span> Le planning n'a pas pu être chargé (${state.planningIndisponible}) : l'état « Planifié » et la mise au planning sont indisponibles jusqu'au rechargement de la page.</p>` : ''}
        <div class="cat-outils" role="search" aria-label="Fouiller le catalogue">
            <div class="cat-recherche">
                <label for="cat-search" class="sr-only">Rechercher un item</label>
                ${icoLocal('magnifying-glass', 18)}
                <input id="cat-search" class="saisie" type="text" value="${q}" placeholder="Numéro, mot du titre ou matière" autocomplete="off" spellcheck="false" enterkeyhint="search" data-input-action="catRecherche" data-enter="catPremier" aria-describedby="cat-compte">
                <button type="button" class="ico-btn cat-effacer" data-action="catEffacer" aria-label="Effacer la recherche"${q ? '' : html` hidden`}>${ico('x', { taille: 18 })}</button>
            </div>
            <div class="cat-rang">
                <label for="cat-score" class="sr-only">Rang</label>
                <span class="select-enveloppe"><select id="cat-score" class="select saisie" data-change-action="catRang">${RANGS.map(function(r) { return html`<option value="${r[0]}"${sc === r[0] ? html` selected` : ''}>${r[1]}</option>`; })}</select></span>
            </div>
            <p id="cat-compte" class="cat-compte num">${texteCompte(items.length, '', 'all')}</p>
            <p id="cat-compte-annonce" class="sr-only" role="status" aria-live="polite" aria-atomic="true"></p>
        </div>
        <div id="cat-body" class="cat-liste" role="list" aria-label="Items du programme">${squeletteLignes()}</div>
    </div>`;
}

// Le focus posé dans le registre (bouton dépliant, action d'une ligne ou d'une sous-partie) survit à un redessin des
// lignes : on retient la ligne et l'action, on les retrouve dans le nouveau rendu.
function memoriserFocusLignes() {
    var a = document.activeElement, b = document.getElementById('cat-body');
    if (!a || !b || !b.contains(a)) return null;
    var row = a.closest('.cat-item'); if (!row) return null;
    var cible = a.closest('[data-action], a[href]');
    return { item: row.getAttribute('data-item'), action: cible ? cible.getAttribute('data-action') : null, args: cible ? (cible.getAttribute('data-args') || '') : '', href: cible && cible.tagName === 'A' ? cible.getAttribute('href') : null };
}
function restaurerFocusLignes(m) {
    if (!m) return;
    var row = document.querySelector('.cat-item[data-item="' + m.item + '"]'); if (!row) return;
    var el = null;
    if (m.action) el = Array.prototype.find.call(row.querySelectorAll('[data-action]'), function(e) { return e.getAttribute('data-action') === m.action && (e.getAttribute('data-args') || '') === m.args; }) || null;
    else if (m.href) el = Array.prototype.find.call(row.querySelectorAll('a[href]'), function(e) { return e.getAttribute('href') === m.href; }) || null;
    if (!el) el = row.querySelector('.cat-item-toggle');
    if (el) { try { el.focus({ preventScroll: true }); } catch (e) {} }
}

// --- les lignes : idempotente, appelée à la première peinture (progressive) et à chaque redessin
export function renderCatalogueList() {
    var b = document.getElementById('cat-body'); if (!b) return;
    if (focusARestaurer) {
        var champ = document.getElementById('cat-search');
        if (champ) { try { champ.focus({ preventScroll: true }); champ.setSelectionRange(focusARestaurer.debut, focusARestaurer.fin); } catch (e) {} }
        focusARestaurer = null;
    }
    var focusLignes = memoriserFocusLignes();
    if (posee) { var age = Date.now() - posee.depuis; if (age < 400) posee.delai = -age; else posee = null; }
    var q = normaliser(state.searchQuery), sc = state.filterScore || 'all', lignes = [];
    window.ednsItems.forEach(function(item) { if (correspond(item, q, sc)) lignes.push(String(ligne(item))); });
    var compte = document.getElementById('cat-compte'), cherche = (state.searchQuery || '').trim();
    var texte = texteCompte(lignes.length, cherche, sc);
    if (compte) compte.textContent = texte;
    annoncerCompte(texte, !!cherche || sc !== 'all');
    if (!lignes.length) {
        b.setAttribute('role', 'presentation');
        b.innerHTML = String(html`<div class="vide">${ico('list-bullets')}<h2>Aucun item ne correspond</h2><p>Essaie un numéro, un mot du titre ou une matière${sc !== 'all' ? ', ou un autre rang' : ''}.</p><button type="button" class="btn btn-fantome" data-action="catToutAfficher">Tout afficher</button></div>`);
        return;
    }
    b.setAttribute('role', 'list');
    window.renderRowsProgressive(b, lignes, 40, 80);
    restaurerFocusLignes(focusLignes);
}
function redessinerLignes() {
    if (renduPrevu) return;
    renduPrevu = requestAnimationFrame(function() { renduPrevu = 0; renderCatalogueList(); });
}
function majEffacer() { var x = document.querySelector('.cat-effacer'); if (x) { if (state.searchQuery) x.removeAttribute('hidden'); else x.setAttribute('hidden', ''); } }

// --- actions de fouille
actions.catRecherche = function(el) { state.searchQuery = el.value; majEffacer(); redessinerLignes(); };
actions.catRang = function(el) { state.filterScore = el.value; renderCatalogueList(); };
actions.catEffacer = function() {
    state.searchQuery = '';
    var champ = document.getElementById('cat-search'); if (champ) champ.value = '';
    majEffacer(); renderCatalogueList(); if (champ) champ.focus();
};
actions.catToutAfficher = function() {
    state.searchQuery = ''; state.filterScore = 'all';
    var champ = document.getElementById('cat-search'), sel = document.getElementById('cat-score');
    if (champ) champ.value = ''; if (sel) sel.value = 'all';
    majEffacer(); renderCatalogueList(); if (champ) champ.focus();
};
// Entrée dans la recherche : le focus saute au premier résultat.
actions.catPremier = function() { var p = document.querySelector('#cat-body .cat-item-toggle'); if (p) p.focus(); };
document.addEventListener('keydown', function(ev) {
    if (ev.key !== 'Escape' || ev.defaultPrevented) return;
    var el = ev.target; if (!el || el.id !== 'cat-search' || !el.value) return;
    ev.preventDefault(); actions.catEffacer();
});

// --- déplier / replier sur place : seules des classes et attributs changent, aucun redessin
actions.catBasculer = function(id, el) {
    var row = el.closest('.cat-item'), detail = row && row.querySelector('.cat-item-detail'); if (!row || !detail) return;
    var ouvrir = !ouverts[id];
    if (ouvrir) ouverts[id] = true; else delete ouverts[id];
    el.setAttribute('aria-expanded', ouvrir ? 'true' : 'false');
    row.classList.toggle('cat-item--ouvert', ouvrir);
    if (ouvrir) detail.removeAttribute('inert'); else detail.setAttribute('inert', '');
    if (ouvrir) setTimeout(function() { try { detail.scrollIntoView({ block: 'nearest', behavior: mouvementReduit() ? 'auto' : 'smooth' }); } catch (e) {} }, mouvementReduit() ? 0 : 190);
};

// --- actions d'un item ou d'une sous-partie
actions.catQcm = function(id, nom) {
    if (nom && typeof nom === 'string') actions.qcmOpenPart(id, encodeURIComponent(nom));
    else actions.qcmOpenItem(id, '', '');
};
actions.catSommaire = function(id, nom) { actions.openSommaire(id, nom && typeof nom === 'string' ? encodeURIComponent(nom) : ''); };
actions.catMenu = function(id, el) {
    var item = window.itemById(id); if (!item) return;
    var parties = partiesDe(item, tachesDe(item)), fiche = window.getFicheDirectLink(id), items = [];
    if (fiche) items.push({ label: 'Ouvrir la fiche PDF', icone: 'file-text', href: fiche, externe: true });
    var patho = window.fichePathoLien(id);
    if (patho) items.push({ label: 'Fiche pathologies', icone: 'tree-structure', href: patho, externe: true, titre: 'La conduite à tenir de chaque pathologie de l\'item, en étapes oui/non' });
    items.push({ label: 'Sommaire pour NotebookLM', icone: 'list-bullets', titre: 'Consigne et sommaire à coller dans un notebook', action: function() { actions.openSommaire(id, ''); } });
    items.push({ label: parties.length ? 'Gérer les sous-parties' : 'Scinder en sous-parties', icone: 'scissors', action: function() { actions.openManageSubParts(id); } });
    ouvrirMenu(el, items, { titre: '#' + id + ' ' + item.titre, label: 'Actions sur l\'item ' + id, feuille: true });
};

// Mettre au planning : un petit formulaire ancré au bouton (feuille basse au doigt), jamais une fenêtre ni un prompt.
// L'état est posé tout de suite (l'aimant « Planifié » arrive sur la ligne), le serveur suit, le toast laisse défaire.
actions.catPlanifier = function(id, pId, titre, el) {
    var item = window.itemById(id); if (!item) return;
    if (planningBloque()) { window.toast('Le planning n\'a pas pu être chargé : recharge la page avant de le modifier', { type: 'danger' }); return; }
    var taches = tachesDe(item), deja = pId ? taches.some(function(t) { return t.itemId === pId; }) : taches.length > 0;
    var saved = pId ? [] : (state.subParts[item.id] || []);
    var quoi = pId ? titre : '#' + id + ' ' + item.titre;
    var aide = deja ? 'Ses séances actuelles sont remplacées par une nouvelle séquence : un J0 à cette date, puis les révisions selon son rang.'
        : (saved.length > 1 ? 'Chaque sous-partie reçoit son J0, une par jour à partir de cette date, puis ses révisions selon le rang de l\'item.' : 'Un J0 à cette date, puis les révisions J1, J3, J7… selon le rang de l\'item.');
    var opts = { titre: (deja ? 'Reprogrammer ' : 'Mettre au planning ') + quoi, aide: aide, label: 'Date du J0', type: 'date', valeur: window.todayStr(), ok: deja ? 'Reprogrammer' : 'Planifier' };
    (el && el.getBoundingClientRect ? ouvrirFormulaire(el, opts) : window.dialogue.demander(opts)).then(function(date) {
        if (!dateValide(date)) return;
        planifier(item, pId, titre, date, deja);
    });
};
function planifier(item, pId, titre, dateStr, deja) {
    var depart = window.parseDateLocal(dateStr), ecarts = window.regleEspacement(item.score), maintenant = Date.now(), idItem = String(item.id);
    var taches = state.tasks.slice(), parties = pId ? [null] : (state.subParts[item.id] || [null]);
    if (pId) taches = taches.filter(function(t) { return t.itemId !== pId; });
    else taches = taches.filter(function(t) { return t.baseItemId !== idItem && t.itemId !== idItem; });
    parties.forEach(function(nomPartie, i) {
        var d0 = new Date(depart); d0.setDate(d0.getDate() + i);
        var idPartie = pId ? pId : (nomPartie ? idItem + '_p' + maintenant + i : idItem);
        var titrePartie = titre ? titre : (nomPartie ? item.titre + ' [' + nomPartie + ']' : item.titre);
        var base = { itemId: idPartie, titre: titrePartie, matiere: item.matiere, score: item.score, status: 'pending', isDouble: false, baseItemId: idItem };
        taches.push(Object.assign({ id: idPartie + '_J0_' + maintenant + '_' + i, type: 'J0', date: window.toYYYYMMDD(d0) }, base));
        ecarts.forEach(function(j) { var d = new Date(d0); d.setDate(d.getDate() + j); taches.push(Object.assign({ id: idPartie + '_J' + j + '_' + maintenant + '_' + i, type: 'J' + j, date: window.toYYYYMMDD(d) }, base)); });
    });
    window.planningAction((deja ? 'Reprogrammation' : 'Mise au planning') + ' de l\'item ' + idItem + (pId && titre ? ' (' + titre + ')' : ''));
    posee = { cle: pId || idItem, depuis: Date.now() };
    window.syncWithServer('save_tasks', { tasks: taches }, function() { renderCatalogueList(); if (window.renderPlanningBar) window.renderPlanningBar(); });
    renderCatalogueList();   // l'état est déjà posé (syncWithServer l'a pris) : l'aimant arrive tout de suite, le serveur suit
    if (window.renderPlanningBar) window.renderPlanningBar();
    refocus('.cat-item[data-item="' + idItem + '"] [data-planifier="' + (pId || idItem) + '"]');
    window.toast((pId ? 'Sous-partie' + (deja ? ' reprogrammée' : ' au planning') : 'Item ' + idItem + (deja ? ' reprogrammé' : ' au planning')) + ' à partir du ' + window.formatDateFr(dateStr), { action: { label: 'Annuler', fn: function() { actions.undoPlanning(); } } });
}

window.renderCatalogue = renderCatalogue;
window.renderCatalogueList = renderCatalogueList;
